import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { DashboardExportButton } from './DashboardExportButton';
import type { DashboardFilters } from '../types/dashboard.types';
import type { DashboardExportEndpoint } from '../api/dashboardExport.api';

interface TableColumn {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  className?: string;
}

interface DashboardTableProps {
  title: string;
  columns: TableColumn[];
  data: Record<string, unknown>[];
  isLoading?: boolean;
  error?: Error | null;
  totals?: Record<string, unknown>;
  exportEndpoint?: DashboardExportEndpoint;
  filters?: DashboardFilters;
}

export const DashboardTable: React.FC<DashboardTableProps> = ({
  title,
  columns,
  data,
  isLoading,
  error,
  totals,
  exportEndpoint,
  filters,
}) => {
  // Debug logging (only in development)
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`🔍 Table "${title}" - Data length:`, data?.length);
      console.log(`🔍 Table "${title}" - Data:`, data);
      console.log(`🔍 Table "${title}" - Columns:`, columns.map(c => c.key));
    }
  }, [data, title, columns]);
  const formatValue = (value: unknown, key: string): string => {
    // Debug logging (only in development)
    if (process.env.NODE_ENV === 'development' && (key === 'alojamientos' || key === 'plazas' || key === 'seg')) {
      console.log(`🔍 Formatting - Key: "${key}", Value:`, value, `Type: ${typeof value}`);
    }
    
    // Handle null, undefined, or empty values
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    
    // Convert string numbers to actual numbers
    if (typeof value === 'string' && !isNaN(Number(value))) {
      value = Number(value);
    }
    
    if (typeof value === 'number') {
      if (key.includes('%')) {
        // Convert to percentage and format with 3 decimals
        return (value * 100).toFixed(3);
      }
      return value.toLocaleString();
    }
    
    // Handle boolean values
    if (typeof value === 'boolean') {
      return value ? 'Sí' : 'No';
    }
    
    return value.toString() || '-';
  };

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg font-semibold text-gray-900">{title}</CardTitle>
            {exportEndpoint && (
              <DashboardExportButton 
                endpoint={exportEndpoint}
                filters={filters}
                disabled={true}
              />
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 p-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full border-red-200">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg font-semibold text-gray-900">{title}</CardTitle>
            {exportEndpoint && (
              <DashboardExportButton 
                endpoint={exportEndpoint}
                filters={filters}
                disabled={true}
              />
            )}
          </div>
        </CardHeader>
        <CardContent className="p-4 text-center text-red-600">
          Error al cargar los datos
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="text-lg font-semibold text-gray-900">{title}</CardTitle>
          {exportEndpoint && (
            <DashboardExportButton 
              endpoint={exportEndpoint}
              filters={filters}
              disabled={isLoading || !!error}
            />
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table className="border border-gray-200">
            <TableHeader className="bg-gray-100">
              <TableRow>
                {columns.map((column) => (
                  <TableHead 
                    key={column.key}
                    className={`text-center font-semibold text-gray-700 ${column.className || ''}`}
                  >
                    {column.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data && data.length > 0 ? (
                data.map((row, index) => {
                  if (process.env.NODE_ENV === 'development') {
                    console.log(`🔍 Rendering row ${index}:`, row);
                  }
                  return (
                    <TableRow key={index} className="hover:bg-gray-50">
                      {columns.map((column) => {
                        const cellValue = row[column.key];
                        if (process.env.NODE_ENV === 'development') {
                          console.log(`🔍 Cell - Row ${index}, Column "${column.key}":`, cellValue);
                        }
                        return (
                          <TableCell 
                            key={column.key}
                            className={`text-center ${
                              column.key.includes('%') ? 'text-blue-600 font-medium' : ''
                            }`}
                          >
                            {formatValue(cellValue, column.key)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="text-center py-8 text-gray-500">
                    {data?.length === 0 ? 'No hay datos disponibles' : 'Cargando datos...'}
                  </TableCell>
                </TableRow>
              )}
              
              {totals && (
                <TableRow className="bg-gray-50 font-semibold border-t-2 border-gray-200">
                  {columns.map((column, index) => (
                    <TableCell key={column.key} className="text-center font-bold text-gray-900">
                      {index === 0
                        ? 'TOTAL'
                        : formatValue(totals[column.key], column.key)
                      }
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};